import React, { useState, useEffect, useRef } from 'react';
import { collection, getDocs, query, orderBy, where, limit } from 'firebase/firestore';
import { db } from '../firebase';
import { Line } from 'react-chartjs-2';
import BaseModal from './common/BaseModal';
import { useFormattedNumber } from '../hooks/useFormattedNumber';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const Dashboard = ({ propertyId }) => {
  const [historyData, setHistoryData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [propertyDetails, setPropertyDetails] = useState(null);
  const [saleSimulation, setSaleSimulation] = useState({
    mortgageBalance: 0,
    salePrice: 0,
    salePercentage: 94,
    totalInvestment: 0,
    netCashIncome: 0,
    cashToClose: 0
  });
  const [netCashIncome, setNetCashIncome] = useState(0);
  const [isDivested, setIsDivested] = useState(false);
  const [divestmentMetrics, setDivestmentMetrics] = useState({
    netProfit: 0,
    totalReturn: 0,
    annualizedReturn: 0,
    hasRequiredData: false
  });
  const mortgageBalanceFormat = useFormattedNumber('');
  const salePriceFormat = useFormattedNumber('');
  const totalInvestmentFormat = useFormattedNumber('');
  const netCashIncomeFormat = useFormattedNumber('');
  const cashToCloseFormat = useFormattedNumber('');

  // Sale simulator state
  const [showSaleSimulator, setShowSaleSimulator] = useState(false);

  useEffect(() => {
    const fetchHistoryData = async () => {
      setIsLoading(true);
      try {
        const historyRef = collection(db, 'properties', propertyId, 'roiHistory');
        const historyQuery = query(historyRef, orderBy('date', 'desc')); // Changed to desc for most recent first
        const snapshot = await getDocs(historyQuery);
        
        const data = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setHistoryData(data);
        setFilteredData(data);
      } catch (error) {
        console.error('Error fetching history data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    const fetchPropertyDetails = async () => {
      try {
        const propertyRef = collection(db, 'properties');
        const propertyQuery = query(propertyRef, where('__name__', '==', propertyId));
        const snapshot = await getDocs(propertyQuery);
        
        if (!snapshot.empty) {
          const propertyData = {
            id: snapshot.docs[0].id,
            ...snapshot.docs[0].data()
          };
          setPropertyDetails(propertyData);
          
          // Check if property is divested
          const isPropertyDivested = propertyData.status === 'DIVESTED';
          setIsDivested(isPropertyDivested);
          
          // If divested, check if we have the necessary data for metrics
          if (isPropertyDivested) {
            const hasRequiredData = propertyData.totalAmountReceived && 
                                    propertyData.saleDate && 
                                    propertyData.closeDate;
            
            if (hasRequiredData) {
              // Calculate net profit
              const netIncome = propertyData.netIncome || 0;
              const totalAmountReceived = Number(propertyData.totalAmountReceived || 0);
              const totalCashInvested = Number(propertyData.totalCashInvested || 0);
              const netProfit = netIncome + totalAmountReceived - totalCashInvested;
              
              // Calculate total return percentage
              const totalReturn = (netProfit / totalCashInvested) * 100;
              
              // Calculate annualized return
              const startDate = new Date(propertyData.closeDate);
              const endDate = new Date(propertyData.saleDate);
              const yearsHeld = (endDate - startDate) / (365 * 24 * 60 * 60 * 1000);
              
              let annualizedReturn = 0;
              if (yearsHeld > 0) {
                annualizedReturn = (Math.pow(1 + (totalReturn / 100), 1 / yearsHeld) - 1) * 100;
              }
              
              setDivestmentMetrics({
                netProfit,
                totalReturn,
                annualizedReturn,
                hasRequiredData: true
              });
            } else {
              setDivestmentMetrics({
                netProfit: 0,
                totalReturn: 0,
                annualizedReturn: 0,
                hasRequiredData: false
              });
            }
          }
          
          // Get the latest ROI history entry for additional metrics
          const historyRef = collection(db, 'properties', propertyId, 'roiHistory');
          const historyQuery = query(historyRef, orderBy('date', 'desc'), limit(1));
          const historySnapshot = await getDocs(historyQuery);
          
          let totalInvestment = 0;
          let netCashIncome = 0;
          
          if (!historySnapshot.empty) {
            const latestHistory = historySnapshot.docs[0].data();
            netCashIncome = Number(latestHistory.netIncome) || 0;
          }
          
          // First try to get totalCashInvested directly from the property
          if (propertyData.totalCashInvested && Number(propertyData.totalCashInvested) > 0) {
            totalInvestment = Number(propertyData.totalCashInvested);
          } 
          // If not available, calculate it from downPayment and closingCosts
          else if (propertyData.downPayment !== undefined || propertyData.closingCosts !== undefined) {
            const downPayment = Number(propertyData.downPayment) || 0;
            const closingCosts = Number(propertyData.closingCosts) || 0;
            totalInvestment = downPayment + closingCosts;
          }
          // Last resort: use cashToClose if available
          else if (propertyData.cashToClose !== undefined) {
            totalInvestment = Number(propertyData.cashToClose) || 0;
          }
          
          // Initialize sale simulator with property data
          mortgageBalanceFormat.onChange({ target: { value: propertyData.mortgageBalance?.toString() || '0' } });
          salePriceFormat.onChange({ target: { value: propertyData.currentValue?.toString() || '0' } });
          
          setSaleSimulation({
            mortgageBalance: Number(propertyData.mortgageBalance) || 0,
            salePrice: Number(propertyData.currentValue) || 0,
            salePercentage: 94,
            totalInvestment: totalInvestment,
            netCashIncome: netCashIncome
          });
          setNetCashIncome(netCashIncome);
        }
      } catch (error) {
        console.error('Error fetching property details:', error);
      }
    };

    fetchHistoryData();
    fetchPropertyDetails();
  }, [propertyId]);

  // Apply date filters
  useEffect(() => {
    if (historyData.length === 0) return;
    
    let filtered = [...historyData];
    
    if (startDate) {
      const startDateObj = new Date(startDate);
      filtered = filtered.filter(item => new Date(item.date) >= startDateObj);
    }
    
    if (endDate) {
      const endDateObj = new Date(endDate);
      endDateObj.setHours(23, 59, 59, 999); // End of the day
      filtered = filtered.filter(item => new Date(item.date) <= endDateObj);
    }
    
    // Sort by date in chronological order (oldest to newest)
    filtered.sort((a, b) => new Date(a.date) - new Date(b.date));
    
    setFilteredData(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [historyData, startDate, endDate]);

  // Prepare chart data for Cash on Cash
  const prepareCashOnCashChartData = () => {
    return {
      labels: filteredData.map(item => new Date(item.date).toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric'
      })),
      datasets: [
        {
          label: 'Cash on Cash (%)',
          data: filteredData.map(item => item.cashOnCash || 0),
          borderColor: 'rgba(153, 102, 255, 1)',
          backgroundColor: 'rgba(153, 102, 255, 0.2)',
          tension: 0.1
        }
      ]
    };
  };

  // Prepare chart data for ROI
  const prepareROIChartData = () => {
    return {
      labels: filteredData.map(item => new Date(item.date).toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric'
      })),
      datasets: [
        {
          label: 'ROI (%)',
          data: filteredData.map(item => item.roi || 0),
          borderColor: 'rgba(75, 192, 192, 1)',
          backgroundColor: 'rgba(75, 192, 192, 0.2)',
          tension: 0.1
        }
      ]
    };
  };

  // Prepare chart data for Net Income
  const prepareNetIncomeChartData = () => {
    return {
      labels: filteredData.map(item => new Date(item.date).toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric'
      })),
      datasets: [
        {
          label: 'Net Income ($)',
          data: filteredData.map(item => item.netIncome || 0),
          borderColor: 'rgba(255, 159, 64, 1)',
          backgroundColor: 'rgba(255, 159, 64, 0.2)',
          tension: 0.1
        }
      ]
    };
  };

  // Prepare chart data for Current Value
  const prepareCurrentValueChartData = () => {
    return {
      labels: filteredData.map(item => new Date(item.date).toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric'
      })),
      datasets: [
        {
          label: 'Current Value ($)',
          data: filteredData.map(item => item.propertyValue || 0),
          borderColor: 'rgba(54, 162, 235, 1)',
          backgroundColor: 'rgba(54, 162, 235, 0.2)',
          tension: 0.1
        }
      ]
    };
  };

  // Get chart options for individual charts
  const getIndividualChartOptions = (title, yAxisLabel, isPercentage = false) => {
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
          labels: {
            color: '#fff'
          }
        },
        title: {
          display: true,
          text: title,
          color: '#fff',
          font: {
            size: 16
          }
        },
        tooltip: {
          mode: 'index',
          intersect: false,
          callbacks: {
            label: function(context) {
              let label = context.dataset.label || '';
              if (label) {
                label += ': ';
              }
              if (context.parsed.y !== null) {
                if (isPercentage) {
                  label += context.parsed.y.toFixed(2) + '%';
                } else {
                  label += '$' + context.parsed.y.toLocaleString();
                }
              }
              return label;
            }
          }
        }
      },
      scales: {
        x: {
          ticks: {
            color: '#ccc'
          },
          grid: {
            color: 'rgba(255, 255, 255, 0.1)'
          }
        },
        y: {
          type: 'linear',
          display: true,
          position: 'left',
          title: {
            display: true,
            text: yAxisLabel,
            color: '#fff'
          },
          ticks: {
            color: '#ccc',
            callback: function(value) {
              if (isPercentage) {
                return value.toFixed(2) + '%';
              } else {
                return '$' + value.toLocaleString();
              }
            }
          },
          grid: {
            color: 'rgba(255, 255, 255, 0.1)'
          }
        }
      }
    };
  };

  const handleDateFilterChange = (e) => {
    const { name, value } = e.target;
    if (name === 'startDate') {
      setStartDate(value);
    } else if (name === 'endDate') {
      setEndDate(value);
    }
  };

  const clearFilters = () => {
    setStartDate('');
    setEndDate('');
  };

  // Pagination logic
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (historyData.length === 0) {
    return (
      <div className="text-center p-5">
        <h4 className="text-muted">No historical data available yet</h4>
        <p>Historical data will appear here as you update property metrics over time.</p>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      {/* Divestment Metrics Banner - Only shown if property is divested */}
      {isDivested && divestmentMetrics.hasRequiredData && (
        <div className="card bg-info text-white mb-4">
          <div className="card-body">
            <div className="d-flex align-items-center mb-2">
              <i className="bi bi-check-circle-fill me-2"></i>
              <h5 className="card-title m-0">Property Divested</h5>
            </div>
            <div className="row g-3">
              <div className="col-md-4">
                <div className="text-center">
                  <h6 className="text-white-50 small mb-1">Net Profit</h6>
                  <div className={`badge fs-5 ${divestmentMetrics.netProfit > 0 ? 'bg-success' : divestmentMetrics.netProfit < 0 ? 'bg-danger' : 'bg-secondary'}`}>
                    ${divestmentMetrics.netProfit.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="text-center">
                  <h6 className="text-white-50 small mb-1">Total Return %</h6>
                  <div className={`badge fs-5 ${divestmentMetrics.totalReturn > 0 ? 'bg-success' : divestmentMetrics.totalReturn < 0 ? 'bg-danger' : 'bg-secondary'}`}>
                    {divestmentMetrics.totalReturn.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}%
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="text-center">
                  <h6 className="text-white-50 small mb-1">Annualized Return</h6>
                  <div className={`badge fs-5 ${divestmentMetrics.annualizedReturn > 0 ? 'bg-success' : divestmentMetrics.annualizedReturn < 0 ? 'bg-danger' : 'bg-secondary'}`}>
                    {divestmentMetrics.annualizedReturn.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}%
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Message when property is divested but missing required data */}
      {isDivested && !divestmentMetrics.hasRequiredData && (
        <div className="card bg-dark border-info text-white mb-4">
          <div className="card-body">
            <div className="d-flex align-items-center mb-2">
              <i className="bi bi-info-circle me-2"></i>
              <h5 className="card-title m-0">Property Divested</h5>
            </div>
            <p className="mb-0">
              To see complete divestment metrics (Net Profit, Total Return %, and Annualized Return), 
              please enter the <strong>Total Amount Received</strong> and <strong>Sale Date</strong> in the property details.
            </p>
          </div>
        </div>
      )}

      {/* Date Filter Controls */}
      <div className="mb-4">
        <div className="d-flex flex-wrap align-items-end justify-content-between gap-3">
          <div className="d-flex flex-wrap align-items-end gap-3">
            <div>
              <label htmlFor="startDate" className="form-label text-white mb-1">Start Date</label>
              <input
                type="date"
                className="form-control bg-dark text-white border-secondary date-input-white-icon"
                id="startDate"
                name="startDate"
                value={startDate}
                onChange={handleDateFilterChange}
              />
            </div>
            <div>
              <label htmlFor="endDate" className="form-label text-white mb-1">End Date</label>
              <input
                type="date"
                className="form-control bg-dark text-white border-secondary date-input-white-icon"
                id="endDate"
                name="endDate"
                value={endDate}
                onChange={handleDateFilterChange}
              />
            </div>
            {(startDate || endDate) && (
              <div>
                <button 
                  className="btn btn-outline-secondary" 
                  onClick={clearFilters}
                >
                  Clear Dates
                </button>
              </div>
            )}
          </div>
          
          <div>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => {
                if (propertyDetails) {
                  setSaleSimulation({
                    mortgageBalance: Number(propertyDetails.mortgageBalance) || 0,
                    salePrice: Number(propertyDetails.currentValue) || 0,
                    salePercentage: 94,
                    totalInvestment: Number(propertyDetails.totalCashInvested) || 0,
                    netCashIncome: netCashIncome || 0
                  });
                  setShowSaleSimulator(true);
                }
              }}
            >
              <i className="bi bi-calculator me-2"></i>Simulate Sale
            </button>
          </div>
        </div>
      </div>

      {filteredData.length > 0 && (
        <>
          {/* Grid layout for charts */}
          <div className="row g-4 mb-4">
            {/* ROI Chart */}
            <div className="col-md-6">
              <div className="card bg-dark border-secondary h-100">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="card-title m-0">ROI Over Time</h6>
                    {filteredData.length > 0 && (
                      <div className={`badge ${filteredData[filteredData.length - 1].roi > 0 ? 'bg-success' : filteredData[filteredData.length - 1].roi < 0 ? 'bg-danger' : 'bg-secondary'} fs-6 me-2`}>
                        {(filteredData[filteredData.length - 1].roi || 0).toFixed(2)}%
                      </div>
                    )}
                  </div>
                  <div style={{ height: '300px' }}>
                    <Line 
                      data={prepareROIChartData()} 
                      options={getIndividualChartOptions('', 'ROI (%)', true)} 
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Cash on Cash Chart */}
            <div className="col-md-6">
              <div className="card bg-dark border-secondary h-100">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="card-title m-0">Cash on Cash Return Over Time</h6>
                    {filteredData.length > 0 && (
                      <div className={`badge ${filteredData[filteredData.length - 1].cashOnCash > 0 ? 'bg-success' : filteredData[filteredData.length - 1].cashOnCash < 0 ? 'bg-danger' : 'bg-secondary'} fs-6`}>
                        {(filteredData[filteredData.length - 1].cashOnCash || 0).toFixed(2)}%
                      </div>
                    )}
                  </div>
                  <div style={{ height: '300px' }}>
                    <Line 
                      data={prepareCashOnCashChartData()} 
                      options={getIndividualChartOptions('', 'Cash on Cash (%)', true)} 
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Net Income Chart */}
            <div className="col-md-6">
              <div className="card bg-dark border-secondary h-100">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="card-title m-0">Net Income Over Time</h6>
                    {filteredData.length > 0 && (
                      <div className={`badge ${filteredData[filteredData.length - 1].netIncome > 0 ? 'bg-success' : filteredData[filteredData.length - 1].netIncome < 0 ? 'bg-danger' : 'bg-secondary'} fs-6`}>
                        ${(filteredData[filteredData.length - 1].netIncome || 0).toLocaleString()}
                      </div>
                    )}
                  </div>
                  <div style={{ height: '300px' }}>
                    <Line 
                      data={prepareNetIncomeChartData()} 
                      options={getIndividualChartOptions('', 'Net Income ($)')} 
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Current Value Chart */}
            <div className="col-md-6">
              <div className="card bg-dark border-secondary h-100">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="card-title m-0">Property Value Over Time</h6>
                    {filteredData.length > 0 && (
                      <div className="badge bg-secondary fs-6">
                        ${(filteredData[filteredData.length - 1].propertyValue || 0).toLocaleString()}
                      </div>
                    )}
                  </div>
                  <div style={{ height: '300px' }}>
                    <Line 
                      data={prepareCurrentValueChartData()} 
                      options={getIndividualChartOptions('', 'Property Value ($)')} 
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Summary Table with Pagination */}
      {filteredData.length > 0 && (
        <div className="mt-5">
          <h4 className="mb-3">Metrics Summary</h4>
          <div className="table-responsive">
            <table className="table table-dark table-striped">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Cash on Cash (%)</th>
                  <th>ROI (%)</th>
                  <th>Net Income ($)</th>
                  <th>Current Value ($)</th>
                  <th>Mortgage Balance ($)</th>
                  <th>Equity ($)</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((item) => (
                  <tr key={item.id}>
                    <td>{item.date ? new Date(item.date).toLocaleDateString() : 'N/A'}</td>
                    <td>{item.cashOnCash !== undefined && item.cashOnCash !== null ? Number(item.cashOnCash).toFixed(2) + '%' : '0.00%'}</td>
                    <td>{item.roi !== undefined && item.roi !== null ? Number(item.roi).toFixed(2) + '%' : '0.00%'}</td>
                    <td>${item.netIncome !== undefined && item.netIncome !== null ? Number(item.netIncome).toLocaleString() : '0'}</td>
                    <td>${item.propertyValue !== undefined && item.propertyValue !== null ? Number(item.propertyValue).toLocaleString() : '0'}</td>
                    <td>${item.mortgageBalance !== undefined && item.mortgageBalance !== null ? Number(item.mortgageBalance).toLocaleString() : '0'}</td>
                    <td>${item.equity !== undefined && item.equity !== null ? Number(item.equity).toLocaleString() : '0'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Pagination Controls */}
          <div className="d-flex justify-content-between align-items-center mt-3">
            <div>
              <button 
                className="btn btn-sm btn-outline-secondary me-2" 
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              >
                Previous
              </button>
              <button 
                className="btn btn-sm btn-outline-secondary" 
                disabled={currentPage * itemsPerPage >= filteredData.length}
                onClick={() => setCurrentPage(prev => prev + 1)}
              >
                Next
              </button>
            </div>
            <div className="text-muted small">
              Showing {Math.min((currentPage - 1) * itemsPerPage + 1, filteredData.length)} to {Math.min(currentPage * itemsPerPage, filteredData.length)} of {filteredData.length} entries
            </div>
          </div>
        </div>
      )}
      
      {/* Sale Simulator Modal */}
      <BaseModal
        isOpen={showSaleSimulator}
        onClose={() => setShowSaleSimulator(false)}
        title="Sale Simulator"
        footer={
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => setShowSaleSimulator(false)}
          >
            Cancel
          </button>
        }
      >
        <div className="p-3">
          <div className="row g-3">
            <div className="col-12">
              <label className="form-label small">Sale Price</label>
              <div className="input-group">
                <span className="input-group-text">$</span>
                <input
                  type="text"
                  className="form-control"
                  value={salePriceFormat.formatted}
                  onChange={(e) => {
                    const numericValue = Number(e.target.value.replace(/[^\d.]/g, '')) || 0;
                    salePriceFormat.onChange(e);
                    setSaleSimulation({
                      ...saleSimulation,
                      salePrice: numericValue
                    });
                  }}
                />
              </div>
            </div>

            <div className="col-12">
              <label className="form-label small">Mortgage Balance</label>
              <div className="input-group">
                <span className="input-group-text">$</span>
                <input
                  type="text"
                  className="form-control"
                  value={mortgageBalanceFormat.formatted}
                  onChange={(e) => {
                    const numericValue = Number(e.target.value.replace(/[^\d.]/g, '')) || 0;
                    mortgageBalanceFormat.onChange(e);
                    setSaleSimulation({
                      ...saleSimulation,
                      mortgageBalance: numericValue
                    });
                  }}
                />
              </div>
            </div>

            <div className="col-12">
              <label className="form-label small">Sale Percentage (after fees, commissions)</label>
              <div className="input-group">
                <input
                  type="number"
                  className="form-control"
                  min="80"
                  max="100"
                  step="0.5"
                  value={saleSimulation.salePercentage}
                  onChange={(e) => {
                    const value = Math.min(100, Math.max(80, Number(e.target.value) || 94));
                    setSaleSimulation({
                      ...saleSimulation,
                      salePercentage: value
                    });
                  }}
                />
                <span className="input-group-text">%</span>
              </div>
            </div>

            <div className="col-12 mt-3">
              <div className="p-2">
                <h6 className="small text-muted mb-3 text-center">Simulation Results</h6>
                
                <div className="row g-2">
                  <div className="col-md-6">
                    <div className="text-center">
                      <div className="text-muted small mb-0">Sale Price</div>
                      <div className="text-white">${Number(saleSimulation.salePrice).toLocaleString()}</div>
                    </div>
                  </div>
                  
                  <div className="col-md-6">
                    <div className="text-center">
                      <div className="text-muted small mb-0">After Fees ({100 - saleSimulation.salePercentage}%)</div>
                      <div className="text-white">${Number(saleSimulation.salePrice * (saleSimulation.salePercentage / 100)).toLocaleString()}</div>
                    </div>
                  </div>
                  
                  <div className="col-md-6">
                    <div className="text-center">
                      <div className="text-muted small mb-0">Mortgage Balance</div>
                      <div className="text-white">${Number(saleSimulation.mortgageBalance).toLocaleString()}</div>
                    </div>
                  </div>
                  
                  <div className="col-md-6">
                    <div className="text-center">
                      <div className="text-muted small mb-0">Net Proceeds</div>
                      <div className="text-white">
                        ${Number(saleSimulation.salePrice * (saleSimulation.salePercentage / 100) - saleSimulation.mortgageBalance).toLocaleString()}
                      </div>
                    </div>
                  </div>
                  
                  <div className="col-12"><hr className="border-secondary my-1" /></div>
                  
                  <div className="col-md-6">
                    <div className="text-center">
                      <div className="text-muted small mb-0">Initial Cash Investment</div>
                      <div className="text-white">${Number(saleSimulation.totalInvestment).toLocaleString()}</div>
                    </div>
                  </div>
                  
                  <div className="col-md-6">
                    <div className="text-center">
                      <div className="text-muted small mb-0">Net Cash Income</div>
                      <div className="text-white">${Number(saleSimulation.netCashIncome).toLocaleString()}</div>
                    </div>
                  </div>
                  
                  <div className="col-12"><hr className="border-secondary my-1" /></div>
                  
                  <div className="col-md-6">
                    <div className="text-center">
                      <div className="text-muted small mb-0">Total Profit</div>
                      {(() => {
                        const netProceeds = saleSimulation.salePrice * (saleSimulation.salePercentage / 100) - saleSimulation.mortgageBalance;
                        const totalProfit = netProceeds - saleSimulation.totalInvestment + saleSimulation.netCashIncome;
                        const profitClass = totalProfit >= 0 ? 'text-success' : 'text-danger';
                        return (
                          <div className={profitClass}>
                            ${Number(totalProfit).toLocaleString()}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                  
                  <div className="col-md-6">
                    <div className="text-center">
                      <div className="text-muted small mb-0">Total ROI</div>
                      {(() => {
                        const netProceeds = saleSimulation.salePrice * (saleSimulation.salePercentage / 100) - saleSimulation.mortgageBalance;
                        const totalProfit = netProceeds - saleSimulation.totalInvestment + saleSimulation.netCashIncome;
                        const totalROI = saleSimulation.totalInvestment > 0 ? (totalProfit / saleSimulation.totalInvestment) * 100 : 0;
                        const roiClass = totalROI >= 0 ? 'text-success' : 'text-danger';
                        return (
                          <div className={roiClass}>
                            {totalROI.toFixed(2)}%
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </BaseModal>

      <style jsx="true">{`
        /* White calendar icon for date inputs */
        input[type="date"]::-webkit-calendar-picker-indicator {
          filter: invert(1);
        }
      `}</style>
    </div>
  );
};

export default Dashboard;
